import React, { useEffect, useMemo } from 'react';
import Loader from '@/components/Generic/Loader';
import PastPerformanceLegend from '@/components/Pages/KidsHub/KidsTable/PastPerformanceChart/PastPerformanceAside';
import PastPerformanceChart from '@/components/Pages/KidsHub/KidsTable/PastPerformanceChart/PastPerformanceChart';
import styles from './PastPerformanceBlock.module.scss';
import { useGetPastPerformanceDataQuery } from '@/store/kidshub/services/kidshubApi';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { Benchmark, PerformanceNotes, PerformanceSet } from 'model/KidsHub';

interface Tooltip {
  note: string;
  year: number;
}

interface ChartInfo {
  columnNames: string[];
  title: string;
  subTitle: string;
  footNote: string;
  extendedFootNote: string;
  benchmarks: Benchmark[];
}

interface Entry {
  year: number;
  columns: { value: number | null }[];
  tooltips: Tooltip[];
}

interface ChartData {
  entries: Entry[];
  chartInfo: ChartInfo;
  tooltips: Tooltip[];
}

interface PastPerformanceBlockProps {
  shareClassId: string;
  countryCode: string;
}

const EMPTY_CHART_DATA: ChartData = {
  entries: [],
  chartInfo: {
    columnNames: [],
    title: '',
    subTitle: '',
    footNote: '',
    extendedFootNote: '',
    benchmarks: [],
  },
  tooltips: [],
};

const populateEntries = (performanceNotes: PerformanceNotes, performanceSets: PerformanceSet[]) => {
  let years: number[] = [];
  const performanceValues = performanceSets.map((set) => set.values).flat();

  performanceValues.forEach((setItem) => years.push(Number(setItem.performanceTimePeriod)));

  years = [...new Set(years)].sort((a, b) => a - b);
  const populatedYears = fillYearsGaps(years);

  populateEmptyYears(populatedYears);

  if (populatedYears.length > 10) {
    populatedYears.splice(0, populatedYears.length - 10);
  }

  return populatedYears.map((year) => ({
    year,
    columns: populateColumns(performanceSets, year),
    tooltips: populateTooltips(performanceNotes.performanceYearsNotes, year),
  }));
};

const fillYearsGaps = (years: number[]) => {
  const noGapsYears: number[] = [];

  for (let i = 0; i < years.length; i++) {
    const iteratedYear = years[i];
    const nextArrayYear = years[i + 1];
    let yearDiff = (nextArrayYear || iteratedYear + 1) - (iteratedYear + 1);

    noGapsYears.push(iteratedYear);

    if (yearDiff) {
      let yearsCounter = iteratedYear + 1;
      while (yearDiff) {
        noGapsYears.push(yearsCounter);
        yearDiff--;
        yearsCounter++;
      }
    }
  }

  return noGapsYears;
};

const populateEmptyYears = (yearsArray: number[]) => {
  if (yearsArray.length >= 10) return;

  const target = yearsArray.length > 5 ? 10 : 5;

  while (yearsArray.length < target) {
    const lastYear = yearsArray[0];
    yearsArray.unshift(lastYear - 1);
  }
};

const populateColumns = (performanceSets: PerformanceSet[], year: number) => {
  return performanceSets
    .map(
      (set) =>
        set.values.find((column) => Number(column.performanceTimePeriod) === year) || {
          value: null,
        }
    )
    .map((col) => ({
      value: col.value !== null ? Number(col.value) : null,
    }));
};

const populateTooltips = (tooltips: Tooltip[], year: number) => {
  const relatedTooltips = tooltips.filter((tooltip) => Number(tooltip.year) === year);
  return relatedTooltips;
};

const populateChartInfo = (
  performanceSets: PerformanceSet[],
  performanceNotes: PerformanceNotes
) => {
  const { title, subTitle, footNote, extendedFootNote, benchmarks } = performanceNotes;

  const columnNames = performanceSets.map((set) => set.performanceLabel);

  return {
    title,
    subTitle,
    footNote,
    extendedFootNote,
    benchmarks,
    columnNames,
  };
};

const PastPerformanceBlock: React.FC<PastPerformanceBlockProps> = ({
  shareClassId,
  countryCode,
}) => {
  const {
    sitecoreContext: { language },
  } = useSitecoreContext();
  const { data, isLoading, isError, error } = useGetPastPerformanceDataQuery({
    shareClassId,
    countryCode,
    language: language!,
  });
  useEffect(() => {
    if (isError) console.error(error);
  }, [isError, error]);

  const chartData: ChartData = useMemo(() => {
    return !!data
      ? {
          tooltips: data.performanceNotes.performanceYearsNotes || [],
          entries: populateEntries(data.performanceNotes, data?.performanceSet),
          chartInfo: populateChartInfo(data.performanceSet, data.performanceNotes),
        }
      : EMPTY_CHART_DATA;
  }, [data]);

  const legendData = chartData.chartInfo.columnNames;

  const tooltips = chartData.entries.length
    ? chartData.tooltips.filter((tooltip) => {
        const chartEntryFirstYear = chartData.entries[0].year;
        const chartEntryLastYear = chartData.entries[chartData.entries.length - 1].year;
        return (
          Number(tooltip.year) >= chartEntryFirstYear && Number(tooltip.year) <= chartEntryLastYear
        );
      })
    : [];

  if (isLoading) {
    return <Loader size={100} className={styles.loader} />;
  }

  return (
    <div>
      <div className="row">
        <div className="col-xl-7">
          <h4 className="subheader-2">{chartData.chartInfo.title}</h4>
          <p className="footnote footnote--bold mt-1">{chartData.chartInfo.subTitle}</p>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-xl-8">
          <PastPerformanceChart chartData={chartData} />
        </div>
        <div className="col-12 col-md-9 col-xl-4 mt-6 mt-xl-0">
          {legendData.length > 0 && (
            <PastPerformanceLegend legendData={legendData} tooltips={tooltips} />
          )}
        </div>
      </div>
      <hr className="my-3" />
      <div className="row">
        <div className="col-xl-8">
          <div className={styles['chart-description']}>
            {chartData.chartInfo.footNote && (
              <p className="footnote footnote--bold">
                <span>{chartData.chartInfo.footNote}</span>
              </p>
            )}
            {chartData.chartInfo.extendedFootNote && (
              <p className="body-small">
                <span>{chartData.chartInfo.extendedFootNote}</span>
              </p>
            )}
            {chartData.chartInfo.benchmarks.map((benchmark, idx) => (
              <p key={idx} className="body-small">
                <span>{benchmark.benchmarkName}</span>
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastPerformanceBlock;
